export type AppErrorCategory = 'business' | 'design' | 'unclassified';

export type AppErrorSeverity = 'info' | 'warning' | 'error';

export type AppErrorDisplayMode = 'snackbar' | 'inline' | 'silent';

/**
 * Internal frontend error contract.
 *
 * This model is intentionally richer than the user-facing message shown in the UI.
 * Components and infrastructure use it to classify failures, choose a presentation
 * channel, and preserve technical context for logging without exposing raw details
 * to end users.
 */
export interface AppError {
  kind: 'app-error';
  name: 'AppError';
  category: AppErrorCategory;
  code: string;
  source: string;
  severity: AppErrorSeverity;
  userMessage: string;
  technicalMessage?: string;
  displayMode: AppErrorDisplayMode;
  retryable: boolean;
  httpStatus?: number;
  endpoint?: string;
  details?: Record<string, unknown>;
  originalError?: unknown;
  timestamp: string;
}

export interface CreateAppErrorInput {
  category: AppErrorCategory;
  code: string;
  source: string;
  severity?: AppErrorSeverity;
  userMessage: string;
  technicalMessage?: string;
  displayMode?: AppErrorDisplayMode;
  retryable?: boolean;
  httpStatus?: number;
  endpoint?: string;
  details?: Record<string, unknown>;
  originalError?: unknown;
}

/**
 * Factory used across the frontend error layer so all emitted errors keep a
 * consistent shape and timestamp.
 */
export function createAppError(input: CreateAppErrorInput): AppError {
  return {
    kind: 'app-error',
    name: 'AppError',
    category: input.category,
    code: input.code,
    source: input.source,
    severity: input.severity ?? 'error',
    userMessage: input.userMessage,
    technicalMessage: input.technicalMessage,
    displayMode: input.displayMode ?? 'snackbar',
    retryable: input.retryable ?? false,
    httpStatus: input.httpStatus,
    endpoint: input.endpoint,
    details: input.details,
    originalError: input.originalError,
    timestamp: new Date().toISOString()
  };
}

/**
 * Narrowing helper for infrastructure code that may receive either native errors,
 * HTTP errors, or already-normalized AppError instances.
 */
export function isAppError(error: unknown): error is AppError {
  return !!error && typeof error === 'object' && (error as AppError).kind === 'app-error';
}